import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import './QueryStateView.scss';

/**
 * QueryErrorState Component
 * Displays an actionable, styled error view with retry support and collapsible technical details.
 *
 * @param {Object} props
 * @param {Error|Object|string} props.error - Error object or message
 * @param {Function} [props.onRetry] - Function to trigger refetch
 * @param {boolean} [props.isRetrying=false] - Whether retry is in progress
 * @param {string} [props.title='Unable to load data']
 * @param {string} [props.className='']
 */
export const QueryErrorState = ({
  error,
  onRetry,
  isRetrying = false,
  title = 'Unable to load data',
  className = '',
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const errorMessage =
    (typeof error === 'string' ? error : error?.message) ||
    error?.response?.data?.message ||
    'An unexpected error occurred while fetching information.';

  const statusCode = error?.status ?? error?.statusCode ?? error?.response?.status;
  const errorDetails = error?.details || error?.response?.data?.errors || error?.stack;

  const handleCopyDetails = () => {
    const textToCopy = JSON.stringify(
      {
        message: errorMessage,
        status: statusCode,
        details: errorDetails,
      },
      null,
      2
    );
    navigator.clipboard?.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`query-state-error ${className}`} role="alert">
      <div className="query-error-header">
        <div className="query-error-icon-wrapper">
          <AlertTriangle className="query-error-icon" size={24} />
        </div>
        <div className="query-error-main">
          <div className="query-error-title-row">
            <h4 className="query-error-title">{title}</h4>
            {statusCode && <span className="query-error-badge">Status {statusCode}</span>}
          </div>
          <p className="query-error-message">{errorMessage}</p>
        </div>
      </div>

      <div className="query-error-actions">
        {onRetry && (
          <button
            type="button"
            className="query-retry-btn"
            onClick={onRetry}
            disabled={isRetrying}
          >
            <RefreshCw size={16} className={isRetrying ? 'animate-spin' : ''} />
            <span>{isRetrying ? 'Retrying...' : 'Try Again'}</span>
          </button>
        )}

        {errorDetails && (
          <button
            type="button"
            className="query-details-toggle"
            onClick={() => setDetailsOpen((prev) => !prev)}
            aria-expanded={detailsOpen}
          >
            <span>{detailsOpen ? 'Hide technical details' : 'Show technical details'}</span>
            {detailsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>

      {detailsOpen && errorDetails && (
        <div className="query-error-details">
          <div className="query-details-toolbar">
            <span className="query-details-label">Debug Stack / API Payload</span>
            <button
              type="button"
              className="query-copy-btn"
              onClick={handleCopyDetails}
              title="Copy error details"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </button>
          </div>
          <pre className="query-details-code">
            {typeof errorDetails === 'string'
              ? errorDetails
              : JSON.stringify(errorDetails, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default QueryErrorState;
